import { useEffect, useRef } from 'react';

type CalendarSize = {
  height: number;
  width: number;
};

const REQUIRED_STABLE_FRAMES = 2;
const SIZE_TOLERANCE_PX = 0.5;
const CALENDAR_APPLICATION_SELECTOR = '[role="application"]';

const hasStableSize = (
  previousSize: CalendarSize | null,
  nextSize: CalendarSize
) =>
  previousSize !== null &&
  Math.abs(previousSize.height - nextSize.height) <= SIZE_TOLERANCE_PX &&
  Math.abs(previousSize.width - nextSize.width) <= SIZE_TOLERANCE_PX;

const isCalendarVisible = (calendar: HTMLElement, size: CalendarSize) => {
  const style = window.getComputedStyle(calendar);

  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    size.height > 0 &&
    size.width > 0
  );
};

/**
 * Produces a shared, semantic readiness signal for react-dates controllers.
 * It deliberately avoids private react-dates class names and waits until the
 * public calendar application has visible, stable geometry for two frames.
 */
export const useCalendarReadiness = (onReady?: () => void) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const onReadyRef = useRef(onReady);
  const isEnabled = Boolean(onReady);

  onReadyRef.current = onReady;

  useEffect(() => {
    if (!isEnabled) return;

    let animationFrameId = 0;
    let lastSize: CalendarSize | null = null;
    let stableFrameCount = 0;

    const checkReadiness = () => {
      const calendar =
        rootRef.current?.querySelector<HTMLElement>(
          CALENDAR_APPLICATION_SELECTOR
        ) ?? null;

      if (calendar) {
        const rect = calendar.getBoundingClientRect();
        const nextSize = {
          height: rect.height,
          width: rect.width,
        };

        if (isCalendarVisible(calendar, nextSize)) {
          stableFrameCount = hasStableSize(lastSize, nextSize)
            ? stableFrameCount + 1
            : 1;
          lastSize = nextSize;

          if (stableFrameCount >= REQUIRED_STABLE_FRAMES) {
            onReadyRef.current?.();
            return;
          }
        } else {
          lastSize = null;
          stableFrameCount = 0;
        }
      }

      animationFrameId = window.requestAnimationFrame(checkReadiness);
    };

    animationFrameId = window.requestAnimationFrame(checkReadiness);

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isEnabled]);

  return rootRef;
};
